<script>
  import { onMount } from "svelte";
  import { fade, fly } from "svelte/transition";
  import state from "./state/state";

  import Navbar from "./Navbar/Navbar.svelte";
  import Sidebar from "./Sidebar/Sidebar.svelte";
  import InfinitiveBox from "./conjugations/components/InfinitiveBox.svelte";
  import NonFiniteFormsBox from "./conjugations/components/NonFiniteFormsBox.svelte";
  import PredicativeBox from "./conjugations/components/PredicativeBox.svelte";
  import InfoModal from "./conjugations/components/InfoModal.svelte";

  import PreteriteBox from "./conjugations/components/PreteriteBox.svelte";
  import VetitiveBox from "./conjugations/components/VetitiveBox.svelte";
  import DurativeBox from "./conjugations/components/DurativeBox.svelte";
  import PerfectBox from "./conjugations/components/PerfectBox.svelte";
  import ImperativeBox from "./conjugations/components/ImperativeBox.svelte";
  import PrecativeBox from "./conjugations/components/PrecativeBox.svelte";

  import settings from "./settings/settings.js";
  import highlightRoot from "./settings/highlightRoot.js";
  import lexicon from "./lexicon/lexicon.js";

  let verbInput = "";
  let verbTag = false;
  let verbInfo = "";
  let lastUpdate = "loading...";

  const validateVerb = verb => {
    const entries = Object.keys(lexicon);
    verbInput = verb.verb;
    if (entries.includes(verbInput)) {
      state.setVerb({
        ...lexicon[verbInput],
        infinitive: verbInput
      });
      const { durativeVowel, themeVowel } = lexicon[verbInput];
      if (durativeVowel) {
        verbInfo = `( ${durativeVowel} - ${themeVowel} )`;
      } else {
        if (themeVowel) {
          verbInfo = `( ${themeVowel} )`;
        } else {
          verbInfo = "-";
        }
      }

      if (lexicon[verbInput].onlyDstem) {
        state.updateView("dstem");
      } else if (lexicon[verbInput].onlySHstem) {
        state.updateView("shstem");
      } else {
        state.updateView("gstem");
      }
    }
  };

  const displayVerbTitle = event => {
    const verbTitle = document.getElementById("verbTitle");
    const verbTitlePos = verbTitle.getBoundingClientRect();
    if (verbTitlePos.top < 0) {
      verbTitle.style.visibility = "hidden";
      verbTag = true;
    } else {
      verbTitle.style.visibility = "visible";
      verbTag = false;
    }
  };

  onMount(async () => {
    // get last update
    const data = await fetch(
      "https://api.github.com/repos/claudebarde/akkadian-conjugator/commits/master"
    );
    const json = await data.json();
    lastUpdate = json.commit.committer.date.split("T")[0];
    // get verb from URL
    const url = window.location; // ?v=šarākum
    const urlObject = new URL(url);
    const v = urlObject.searchParams.get("v");
    // we display verb if exists
    const entries = Object.keys(lexicon);
    if (entries.includes(v)) {
      validateVerb({ verb: v });
    }
  });
</script>

<style>
  main {
    padding: 0px 20px;
    height: 100%;
    overflow: hidden;
  }

  .welcome {
    margin-top: 50px;
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: center;
  }

  #scrolledTitle {
    position: fixed;
    margin-top: 10px;
    padding: 0px;
    /*border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;*/
    z-index: 20;
  }

  .tables {
    margin-top: 20px;
    overflow: auto;
  }

  .lastColumns {
    padding-bottom: 50px;
  }

  .conjugatorOptions {
    text-align: center;
  }

  .mainColumn {
    overflow-y: auto;
    overflow-x: hidden;
    height: 95vh;
  }

  .mainColumn::-webkit-scrollbar {
    width: 0px; /* Remove scrollbar space */
    background: transparent; /* Optional: just make scrollbar invisible */
  }

  .errorWarning {
    text-align: left;
    max-width: 500px;
  }

  .alert-icon {
    vertical-align: bottom;
    margin-right: 10px;
  }
</style>

<Navbar
  on:selectVerb={event => {
    const verb = event.detail;
    validateVerb(verb);
  }} />
<main>
  <div class="columns">
    <div class="column is-hidden-touch is-2">
      <Sidebar
        on:selectVerb={event => {
          const verb = event.detail;
          validateVerb(verb);
        }} />
    </div>
    {#if !verbInput}
      <div class="column is-10 welcome">
        <h1 class="title is-5">Select a verb in the left panel to start.</h1>
        <h2 class="subtitle is-6">Last update: {lastUpdate}</h2>
        <br />
        <div class="notification is-warning errorWarning is-size-7-mobile">
          <button class="delete" />
          Please be aware that the verbal forms in the app are autogenerated
          following the known rules of verbal formation in Old Babylonian.
          <br />
          Thus, some irregular and rare forms may be missing and some unattested
          forms can appear, although they will be notified as much as possible
          <br />
          If you notice an error, please contact me through the following
          contact form.
          <br />
          Thank you!
        </div>
      </div>
    {/if}
    <div class="column is-10">
      {#if verbTag}
        <div id="scrolledTitle" transition:fade>
          <div class="control">
            <div class="tags has-addons">
              <span class="tag is-dark">{verbInput.toUpperCase()}</span>
              <span class="tag is-info">{verbInfo}</span>
              <span class="tag">
                {#if $state.activeView === 'gstem'}
                  G Stem
                {:else if $state.activeView === 'dstem'}
                  D Stem
                {:else if $state.activeView === 'shstem'}Š Stem{/if}
              </span>
            </div>
          </div>
        </div>
      {/if}
      {#if verbInput}
        <div class="mainColumn" on:scroll={displayVerbTitle}>
          <div class="columns">
            <div class="column is-6 is-offset-3">
              <InfinitiveBox />
            </div>
          </div>
          <div class="columns">
            <div class="column is-3 is-offset-3 conjugatorOptions">
              <label class="checkbox is-size-7-mobile">
                <input
                  type="checkbox"
                  checked={$state.rootHighlight}
                  on:change={state.toggleRootHighlighting} />
                Root highlighting
              </label>
            </div>
            <div class="column is-3 conjugatorOptions">
              <label class="checkbox is-size-7-mobile">
                <input
                  type="checkbox"
                  checked={$state.ventive}
                  on:change={state.toggleVentive} />
                Ventive
              </label>
            </div>
          </div>
          <!-- COMPUTER VERSION -->
          <div class="tabs is-small is-toggle is-fullwidth is-hidden-mobile">
            <ul>
              {#if $state.onlyDstem || $state.onlySHstem}
                <li>
                  <a href="#">
                    <span>G Stem (none)</span>
                  </a>
                </li>
              {:else}
                <li
                  class:is-active={$state.activeView === 'gstem'}
                  on:click={() => state.updateView('gstem')}>
                  <a href="#">
                    <span>G Stem</span>
                  </a>
                </li>
              {/if}
              {#if $state.onlyGstem}
                <li>
                  <a href="#">
                    <span>D Stem (none)</span>
                  </a>
                </li>
                <li>
                  <a href="#">
                    <span>Š Stem (none)</span>
                  </a>
                </li>
                <li>
                  <a href="#">
                    <span>N Stem (none)</span>
                  </a>
                </li>
              {:else}
                {#if $state.onlySHstem}
                  <li>
                    <a href="#">
                      <span>D Stem (none)</span>
                    </a>
                  </li>
                {:else}
                  <li
                    class:is-active={$state.activeView === 'dstem'}
                    on:click={() => state.updateView('dstem')}>
                    <a href="#">
                      <span>D Stem</span>
                    </a>
                  </li>
                {/if}
                <li
                  class:is-active={$state.activeView === 'shstem'}
                  on:click={() => state.updateView('shstem')}>
                  <a href="#">
                    <span>Š Stem</span>
                  </a>
                </li>
                <li
                  class:is-active={$state.activeView === 'nstem'}
                  on:click={() => state.updateView('nstem')}>
                  <a href="#">
                    <span>N Stem</span>
                  </a>
                </li>
              {/if}
            </ul>
          </div>
          <!-- MOBILE VERSION -->
          <div class="tabs is-small is-toggle is-fullwidth is-hidden-tablet">
            <ul>
              <li
                class:is-active={$state.activeView === 'gstem'}
                on:click={() => state.updateView('gstem')}>
                <a href="#">
                  <span>G Stem</span>
                </a>
              </li>
              <li
                class:is-active={$state.activeView === 'dstem'}
                on:click={() => state.updateView('dstem')}>
                <a href="#">
                  <span>D Stem</span>
                </a>
              </li>
              <li
                class:is-active={$state.activeView === 'shstem'}
                on:click={() => state.updateView('shstem')}>
                <a href="#">
                  <span>Š Stem</span>
                </a>
              </li>
              <li
                class:is-active={$state.activeView === 'nstem'}
                on:click={() => state.updateView('nstem')}>
                <a href="#">
                  <span>N Stem</span>
                </a>
              </li>
            </ul>
          </div>
          <br />
          {#if $state.activeView === 'gstem'}
            <div
              in:fly={{ x: -100, duration: 500, delay: 200 }}
              out:fly={{ x: 100, duration: 200 }}>
              <div class="columns tables">
                <div class="column is-two-fifths is-offset-1">
                  <PreteriteBox />
                </div>
                <div class="column is-two-fifths">
                  <DurativeBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-two-fifths is-offset-1">
                  <PerfectBox />
                </div>
                <div class="column is-two-fifths">
                  <VetitiveBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-3 is-offset-2">
                  <ImperativeBox />
                </div>
                <div class="column is-4 is-offset-1">
                  <PrecativeBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-two-thirds is-offset-2">
                  <NonFiniteFormsBox />
                </div>
              </div>
              <div class="columns lastColumns">
                <div class="column is-half is-offset-3">
                  <PredicativeBox />
                </div>
              </div>
            </div>
          {:else if $state.activeView === 'dstem'}
            <div
              class="message is-warning"
              in:fly={{ x: -100, duration: 500, delay: 200 }}
              out:fly={{ x: 100, duration: 200 }}>
              <div class="message-body">
                <img
                  src="images/alert-triangle.svg"
                  alt="alert-icon"
                  class="alert-icon" />
                <strong>
                  Please be aware that this section is under construction.
                </strong>
              </div>
            </div>
            <div
              in:fly={{ x: -100, duration: 500, delay: 200 }}
              out:fly={{ x: 100, duration: 200 }}>
              <div class="columns tables">
                <div class="column is-two-fifths is-offset-1">
                  <PreteriteBox />
                </div>
                <div class="column is-two-fifths">
                  <DurativeBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-two-fifths is-offset-1">
                  <PerfectBox />
                </div>
                <div class="column is-two-fifths">
                  <VetitiveBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-3 is-offset-2">
                  <ImperativeBox />
                </div>
                <div class="column is-4 is-offset-1">
                  <PrecativeBox />
                </div>
              </div>
              <div class="columns lastColumns">
                <div class="column is-four-fifths is-offset-1">
                  <NonFiniteFormsBox />
                </div>
              </div>
            </div>
          {:else if $state.activeView === 'shstem'}
            <div
              class="message is-warning"
              in:fly={{ x: -100, duration: 500, delay: 200 }}
              out:fly={{ x: 100, duration: 200 }}>
              <div class="message-body">
                <img
                  src="images/alert-triangle.svg"
                  alt="alert-icon"
                  class="alert-icon" />
                <strong>
                  Please be aware that this section is under construction.
                </strong>
              </div>
            </div>
            <div
              in:fly={{ x: -100, duration: 500, delay: 200 }}
              out:fly={{ x: 100, duration: 200 }}>
              <div class="columns tables">
                <div class="column is-two-fifths is-offset-1">
                  <PreteriteBox />
                </div>
                <div class="column is-two-fifths">
                  <DurativeBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-two-fifths is-offset-1">
                  <PerfectBox />
                </div>
                <div class="column is-two-fifths">
                  <VetitiveBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-3 is-offset-2">
                  <ImperativeBox />
                </div>
                <div class="column is-4 is-offset-1">
                  <PrecativeBox />
                </div>
              </div>
              <div class="columns lastColumns">
                <div class="column is-four-fifths is-offset-1">
                  <NonFiniteFormsBox />
                </div>
              </div>
            </div>
          {:else if $state.activeView === 'nstem'}
            <div
              class="message is-warning"
              in:fly={{ x: -100, duration: 500, delay: 200 }}
              out:fly={{ x: 100, duration: 200 }}>
              <div class="message-body">
                <img
                  src="images/alert-triangle.svg"
                  alt="alert-icon"
                  class="alert-icon" />
                <strong>
                  Please be aware that this section is under construction.
                </strong>
              </div>
            </div>
            <div
              in:fly={{ x: -100, duration: 500, delay: 200 }}
              out:fly={{ x: 100, duration: 200 }}>
              <div class="columns tables">
                <div class="column is-two-fifths is-offset-1">
                  <PreteriteBox />
                </div>
                <div class="column is-two-fifths">
                  <DurativeBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-two-fifths is-offset-1">
                  <PerfectBox />
                </div>
                <div class="column is-two-fifths">
                  <VetitiveBox />
                </div>
              </div>
              <div class="columns">
                <div class="column is-3 is-offset-2">
                  <ImperativeBox />
                </div>
                <div class="column is-4 is-offset-1">
                  <PrecativeBox />
                </div>
              </div>
              <div class="columns lastColumns">
                <div class="column is-four-fifths is-offset-1">
                  <NonFiniteFormsBox />
                </div>
              </div>
            </div>
          {:else}
            <div
              class="message is-warning"
              in:fly={{ x: -100, duration: 500, delay: 200 }}
              out:fly={{ x: 100, duration: 200 }}>
              <div class="message-body">
                <img
                  src="images/alert-triangle.svg"
                  alt="alert-icon"
                  class="alert-icon" />
                <strong>
                  Please be aware that this section is under construction.
                </strong>
              </div>
            </div>
          {/if}
        </div>
      {/if}
    </div>
  </div>
</main>
<InfoModal />
